<template>
  <div :class="['UICarousel', { 'UICarousel--dragging': isDragging }]">
    <div ref="viewportRef" class="UICarousel__viewport">
      <div
        class="UICarousel__edge-fade UICarousel__edge-fade--left"
        aria-hidden="true"
        v-show="!isAtStart"
      />
      <div
        class="UICarousel__edge-fade UICarousel__edge-fade--right"
        aria-hidden="true"
        v-show="!isAtEnd"
      />
      <ul ref="trackRef" class="UICarousel__track" :style="trackStyle">
        <li
          v-for="(item, index) in items"
          :key="index"
          class="UICarousel__slide"
        >
          <slot name="card" :item="item" :index="index">
            <div class="UICarousel__default-card">
              <h4 class="UICarousel__default-card-title">
                {{ item.title }}
              </h4>
              <p class="UICarousel__default-card-description">
                {{ item.description }}
              </p>
            </div>
          </slot>
        </li>
      </ul>
    </div>

    <div class="UICarousel__controls">
      <button
        class="UICarousel__nav UICarousel__nav--prev"
        type="button"
        :disabled="isAtStart"
        @click="scrollPrev"
        aria-label="Previous"
      >
        <UIIcon name="arrowLeft" />
      </button>
      <button
        class="UICarousel__nav UICarousel__nav--next"
        type="button"
        :disabled="isAtEnd"
        @click="scrollNext"
        aria-label="Next"
      >
        <UIIcon name="arrowRight" />
      </button>
    </div>
  </div>
  <div class="UICarousel__dots" v-if="showDots && items.length > 1">
    <button
      v-for="(n, dotIndex) in totalPages"
      :key="dotIndex"
      type="button"
      :class="[
        'UICarousel__dot',
        { 'UICarousel__dot--active': dotIndex === currentPage },
      ]"
      @click="scrollToPage(dotIndex)"
      aria-label="Go to slide group"
    />
  </div>
</template>

<script setup lang="ts">
import { onMounted, onBeforeUnmount, ref, computed } from 'vue'
import UIIcon from '@/views/components/ui/icons/UIIcon.vue'

export type Item = {
  title?: string
  description?: string
  image?: string
  // allow extra attributes without losing the string fields above
  [key: string]: unknown
}

withDefaults(
  defineProps<{
    items: Item[]
    showDots?: boolean
  }>(),
  {
    items: () => [],
    showDots: false,
  },
)

defineSlots<{
  card?: (slotProps: { item: Item; index: number }) => any
}>()

const viewportRef = ref<HTMLDivElement | null>(null)
const trackRef = ref<HTMLUListElement | null>(null)
const isAtStart = ref(true)
const isAtEnd = ref(false)
const currentIndex = ref(0)

// Drag state
const isDragging = ref(false)
const dragStartX = ref(0)
const dragDeltaX = ref(0)
const dragBaseLeft = ref(0)
const DRAG_THRESHOLD_PX = 40

const getSlides = () =>
  Array.from((trackRef.value?.children || []) as unknown as HTMLElement[])

const getOffsets = () => getSlides().map((el) => el.offsetLeft)
const slideOffsets = ref<number[]>([])

const totalPages = computed(() => {
  const total = getSlides().length
  return Math.max(total, 1)
})

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max)

const currentPage = computed(() => currentIndex.value)

const updateNavState = () => {
  const offsets = slideOffsets.value
  const idx = clamp(currentIndex.value, 0, Math.max(offsets.length - 1, 0))
  currentIndex.value = idx
  isAtStart.value = idx === 0
  isAtEnd.value = idx >= Math.max(offsets.length - 1, 0)
}

const leftForIndex = (index: number) => {
  const offsets = slideOffsets.value
  // 16px = --edge-bleed default
  return Math.max((offsets[index] || 0) - 16, 0)
}

const trackStyle = computed(() => {
  const baseLeft = leftForIndex(currentIndex.value)
  const dragOffset = isDragging.value ? dragDeltaX.value : 0
  const maxLeft = leftForIndex(Math.max(slideOffsets.value.length - 1, 0))
  const left = clamp(baseLeft + dragOffset, 0, Math.max(maxLeft, 0))
  return {
    transform: `translateX(-${left}px)`,
    transition: isDragging.value ? 'none' : undefined,
  }
})

// Scroll to a specific slide index using measured left offsets
const scrollToIndex = (index: number) => {
  const offsets = slideOffsets.value
  if (!offsets.length) return
  const idx = clamp(index, 0, offsets.length - 1)
  currentIndex.value = idx
  updateNavState()
}

const scrollPrev = () => scrollToIndex(currentIndex.value - 1)
const scrollNext = () => scrollToIndex(currentIndex.value + 1)
const scrollToPage = (pageIndex: number) => scrollToIndex(pageIndex)

const recomputeOffsets = () => {
  slideOffsets.value = getOffsets()
  updateNavState()
}

const onResize = () => recomputeOffsets()

onMounted(() => {
  recomputeOffsets()
  window.addEventListener('resize', onResize)
  // Pointer events for drag
  viewportRef.value?.addEventListener('pointerdown', onPointerDown)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize)
  viewportRef.value?.removeEventListener('pointerdown', onPointerDown)
  window.removeEventListener('pointermove', onPointerMove)
  window.removeEventListener('pointerup', onPointerUp)
  window.removeEventListener('pointercancel', onPointerUp)
})

// Drag helpers
const onPointerDown = (e: PointerEvent) => {
  // For mouse, only respond to primary button; for touch/pen always allow
  if (e.pointerType === 'mouse' && e.button !== 0) return
  isDragging.value = true
  dragStartX.value = e.clientX
  dragDeltaX.value = 0
  dragBaseLeft.value = leftForIndex(currentIndex.value)

  try {
    viewportRef.value?.setPointerCapture(e.pointerId)
  } catch (_) {
    // no-op if not supported
  }

  window.addEventListener('pointermove', onPointerMove, { passive: true })
  window.addEventListener('pointerup', onPointerUp)
  window.addEventListener('pointercancel', onPointerUp)
}

const onPointerMove = (e: PointerEvent) => {
  if (!isDragging.value) return
  // Negative when dragging right, positive when dragging left
  const deltaX = e.clientX - dragStartX.value
  dragDeltaX.value = -deltaX
}

const findNearestIndexForLeft = (left: number) => {
  const count = slideOffsets.value.length
  if (count <= 1) return 0
  let nearest = 0
  let best = Number.POSITIVE_INFINITY
  for (let i = 0; i < count; i++) {
    const L = leftForIndex(i)
    const diff = Math.abs(L - left)
    if (diff < best) {
      best = diff
      nearest = i
    }
  }

  return nearest
}

const onPointerUp = () => {
  if (!isDragging.value) return
  const delta = dragDeltaX.value
  isDragging.value = false
  dragDeltaX.value = 0

  // Decide navigation: threshold or snap to nearest
  if (Math.abs(delta) > DRAG_THRESHOLD_PX) {
    if (delta > 0 && !isAtEnd.value) {
      scrollNext()
    } else if (delta < 0 && !isAtStart.value) {
      scrollPrev()
    } else {
      // Snap back if at bounds
      scrollToIndex(currentIndex.value)
    }
  } else {
    const proposedLeft = dragBaseLeft.value + delta
    const target = findNearestIndexForLeft(proposedLeft)
    scrollToIndex(target)
  }

  window.removeEventListener('pointermove', onPointerMove)
  window.removeEventListener('pointerup', onPointerUp)
  window.removeEventListener('pointercancel', onPointerUp)
}
</script>

<style scoped lang="scss">
.UICarousel {
  position: relative;
  overflow: hidden;
  cursor: grab;
  padding-bottom: 1rem;
  width: 100%;
  // Fallback to white so fades blend even if parent doesn't provide a color
  --UICarousel-edge-fade-rgb: #{$color-white};

  .UICarousel__viewport {
    position: relative;
    // Allow vertical scrolling of the page while capturing horizontal drags
    touch-action: pan-y;
    overflow-y: visible;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    // Hide scrollbar visuals while keeping scroll functionality
    -ms-overflow-style: none; // IE/Edge
    scrollbar-width: none; // Firefox
    &::-webkit-scrollbar {
      // WebKit
      display: none;
    }
    // Allow bottom shadows to be visible without affecting layout height
    // Clearance roughly matches the largest shadow spread/blur
    padding-bottom: 1.75rem;
    margin-bottom: -1.75rem;

    // Provide inline bleed so first/last card shadows are visible
    --edge-bleed: 1rem;
    padding-left: var(--edge-bleed);
    padding-right: var(--edge-bleed);
    margin-left: calc(-1 * var(--edge-bleed));
    margin-right: calc(-1 * var(--edge-bleed));
  }

  .UICarousel__track {
    display: inline-flex;
    align-items: stretch;
    gap: 1rem;
    padding: 0.25rem 0.5rem; // breathing room for focus ring
    list-style: none;
    transition: transform 400ms ease; // smooth slide animation
  }

  .UICarousel__slide {
    scroll-snap-align: start;
    flex: 0 0 auto;
    min-width: var(--slide-width, 18rem);
    // avoid additional overflow clipping from descendants
    overflow: visible;
  }

  .UICarousel__controls {
    display: flex;
    gap: 0.5rem;
    margin-top: 2rem;
    width: 100%;
  }

  .UICarousel__nav {
    @include trans;
    display: grid;
    place-items: center;
    width: 3.5rem;
    height: 3.5rem;
    border-radius: 999px;
    border: 1px solid $newcolor-border-light;
    background: $color-white;
    box-shadow: 0 2px 8px rgba(16, 24, 40, 0.08);

    &:disabled {
      opacity: 0.4;
      pointer-events: none;
    }
  }
}

.UICarousel__edge-fade {
  position: absolute;
  top: 0;
  bottom: 0;
  width: var(--edge-fade, 115px);
  pointer-events: none;
  z-index: 2;
  background: var(--UICarousel-edge-fade-rgb, #{$color-white});
  mask-repeat: no-repeat;
  mask-size: 100% 100%;
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-size: 100% 100%;
}

.UICarousel__edge-fade--left {
  left: 1rem;
  mask-image: linear-gradient(
    to right,
    rgba(0, 0, 0, 1) 0%,
    rgba(0, 0, 0, 0) 100%
  );
  -webkit-mask-image: linear-gradient(
    to right,
    rgba(0, 0, 0, 1) 0%,
    rgba(0, 0, 0, 0) 100%
  );
}

.UICarousel__edge-fade--right {
  right: 1rem;
  mask-image: linear-gradient(
    to left,
    rgba(0, 0, 0, 1) 0%,
    rgba(0, 0, 0, 0) 100%
  );
  -webkit-mask-image: linear-gradient(
    to left,
    rgba(0, 0, 0, 1) 0%,
    rgba(0, 0, 0, 0) 100%
  );
}

.UICarousel--dragging {
  cursor: grabbing;
  -webkit-user-select: none;
  user-select: none;
}

.UICarousel__dots {
  display: flex;
  gap: 0.375rem;
  justify-content: center;
  margin-top: 0.75rem;

  .UICarousel__dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    border: none;
    background: $newcolor-grey-300;
    padding: 0;
    cursor: pointer;

    &--active {
      background: $newcolor-primary-main;
    }
  }
}

// Responsive slide widths
@include md {
  .UICarousel__slide {
    --slide-width: 20rem;
  }
}

@include lg {
  .UICarousel__slide {
    --slide-width: 22rem;
  }
}

// Default card (used if no slot is provided)
.UICarousel__default-card {
  border-radius: 0.75rem;
  background: $color-white;
  border: none;
  box-shadow:
    0px 3px 3px -1.5px var(--ColorsEffectsShadowsshadow-xl03),
    0px 8px 8px -4px var(--ColorsEffectsShadowsshadow-xl02),
    0px 20px 24px -4px var(--ColorsEffectsShadowsshadow-xl01);
  padding: 1rem;

  .UICarousel__default-card-title {
    font-weight: 600;
    margin-bottom: 0.25rem;
  }
  .UICarousel__default-card-description {
    color: $newcolor-grey-600;
    font-size: 0.875rem;
  }
}
</style>
